import React, { useMemo, useState, useCallback } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaListUl } from 'react-icons/fa';
import ResizableTable from '../common/ResizableTable';
import { prepareTableData, getFilteredAndSortedRecords } from './utils/listViewUtils';
import { useSearchViewWidgetData } from './hooks/useSearchViewWidgetData';
import { theme } from '../../theme/theme.js';
import SearchViewShowAllModal from './SearchViewShowAllModal';
import {
  normalizeAllowedRelatedListActions,
  buildRelatedListRowActions,
  buildRelatedListHeaderActions
} from '../../utils/buildRelatedListTableActions';

/**
 * Tabular search-view results: same ResizableTable mode as related lists (sourcePage relatedlist) and
 * "Show all" opens the same CommonModal shell as RelatedListView.
 */
const SearchViewRecordsTable = ({
  widget,
  recordData,
  currentRecordId,
  sourceObjectName,
  maxHeight = 560,
  className = '',
  /** Related-tabs cache: restore without network when revisiting the tab */
  restoredSnapshot = null,
  onSnapshotCommit = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAllModal, setShowAllModal] = useState(false);

  const {
    searchViewConfig,
    records,
    loading,
    error,
    fieldDataTypes,
    fieldMetadata,
    searchObjectName,
    searchObjectLabel,
    widgetConfig,
    selectedFields,
    executeSearch
  } = useSearchViewWidgetData(widget, recordData, {
    restoredSnapshot,
    onSnapshotCommit
  });

  const searchViewId = widgetConfig?.search_view_id;

  const searchPlaceholder = useMemo(() => {
    const noun = (searchObjectLabel || searchObjectName || 'records').toLowerCase();
    return `Search my ${noun}...`;
  }, [searchObjectLabel, searchObjectName]);

  const showAllButtonLabel = useMemo(() => {
    const vl =
      searchViewConfig?.view_label ||
      searchViewConfig?.search_view_label ||
      widgetConfig?.search_view_label;
    const slug = searchViewConfig?.search_view_name || widgetConfig?.search_view_name;
    const title = vl || (slug ? String(slug).replace(/_/g, ' ') : '');
    if (title) {
      return `Show all ${title}`;
    }
    if (searchObjectLabel) {
      return `Show all ${searchObjectLabel}`;
    }
    return 'Show all';
  }, [searchViewConfig, widgetConfig, searchObjectLabel]);

  const fullSearchModalTitle = useMemo(() => {
    return (
      searchViewConfig?.view_label ||
      searchViewConfig?.search_view_label ||
      searchObjectLabel ||
      searchObjectName ||
      'Search'
    );
  }, [searchViewConfig, searchObjectLabel, searchObjectName]);

  const openShowAllModal = useCallback(() => {
    setShowAllModal(true);
  }, []);

  const displayFieldsArray = useMemo(() => {
    if (!searchViewConfig?.display_fields) return [];
    if (Array.isArray(searchViewConfig.display_fields)) {
      return searchViewConfig.display_fields;
    }
    if (typeof searchViewConfig.display_fields === 'string') {
      try {
        return JSON.parse(searchViewConfig.display_fields);
      } catch {
        return searchViewConfig.display_fields.split(',').map((f) => f.trim()).filter(Boolean);
      }
    }
    return [];
  }, [searchViewConfig]);

  const columnFieldNames = useMemo(() => {
    const base =
      selectedFields.length > 0 ? selectedFields : displayFieldsArray;
    return base.filter((f) => f && f !== 'id');
  }, [selectedFields, displayFieldsArray]);

  const columns = useMemo(() => {
    return columnFieldNames.map((fieldName) => {
      const meta = fieldMetadata[fieldName] || {};
      const dataType = (fieldDataTypes[fieldName] || 'text').toLowerCase();
      const isNumeric = ['currency', 'number', 'decimal', 'integer', 'rollupsummary'].includes(
        dataType
      );
      return {
        field: fieldName,
        label: meta.field_label || fieldName,
        sortable: true,
        dataType,
        align: isNumeric ? 'right' : 'left'
      };
    });
  }, [columnFieldNames, fieldMetadata, fieldDataTypes]);

  const tableData = useMemo(() => {
    if (!searchObjectName || !records?.length) {
      return prepareTableData(records || [], searchObjectName || 'object', fieldDataTypes);
    }
    return prepareTableData(records, searchObjectName, fieldDataTypes);
  }, [records, searchObjectName, fieldDataTypes]);

  const filteredSorted = useMemo(
    () => getFilteredAndSortedRecords(tableData, searchTerm, {}, sortBy, sortOrder),
    [tableData, searchTerm, sortBy, sortOrder]
  );

  const allowedActions = useMemo(
    () => normalizeAllowedRelatedListActions(widgetConfig?.actions, searchObjectName),
    [widgetConfig?.actions, searchObjectName]
  );

  const rowActions = useMemo(
    () => buildRelatedListRowActions(allowedActions, { objectName: searchObjectName }),
    [allowedActions, searchObjectName]
  );

  const headerActions = useMemo(
    () =>
      buildRelatedListHeaderActions(allowedActions, {
        isAttachmentTab: false,
        isHistoryObject: false,
        selectedCount: selectedRows.length
      }),
    [allowedActions, selectedRows.length]
  );

  const recordLinkField = useMemo(() => {
    if (columnFieldNames.map((f) => f.toLowerCase()).includes('name')) {
      return 'name';
    }
    return columnFieldNames[0] || 'name';
  }, [columnFieldNames]);

  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  if (!searchViewId) {
    return (
      <Alert variant="warning" className="mb-0">
        Search view not configured for this widget.
      </Alert>
    );
  }

  if (!searchViewConfig) {
    return (
      <div className="text-center py-4 text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading search view…
      </div>
    );
  }

  if (!searchObjectName) {
    return (
      <Alert variant="warning" className="mb-0">
        Search view object not found.
      </Alert>
    );
  }

  if (error && !loading) {
    return (
      <Alert variant="danger" className="mb-0">
        {error}
      </Alert>
    );
  }

  return (
    <div
      className={`search-view-records-table-wrapper mb-0 ${className}`}
      style={{
        border: '1px solid #dee2e6',
        borderRadius: 4,
        overflow: 'hidden',
        background: '#fff'
      }}
    >
      <div className="p-0" style={{ maxHeight, overflow: 'auto' }}>
        <div className="search-view-records-table px-2 py-2">
          <ResizableTable
            data={filteredSorted}
            columns={columns}
            loading={loading}
            error={null}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            currentPage={1}
            totalRecords={filteredSorted.length}
            recordsPerPage={filteredSorted.length}
            onPageChange={() => {}}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            showSerialNumber
            showActions={rowActions.length > 0}
            actions={rowActions}
            headerActions={headerActions}
            selectedRows={selectedRows}
            onRowSelectionChange={setSelectedRows}
            exportFilename={`${searchObjectName}_search_widget`}
            exportSheetName={widget?.widgetLabel || 'search'}
            className="related-list-view-table"
            tableClassName="table-theme"
            emptyMessage="No records found"
            showSearch
            showPagination={false}
            searchPlaceholder={searchPlaceholder}
            objectName={searchObjectName}
            sourcePage="relatedlist"
            onRefresh={() => executeSearch()}
            recordId={currentRecordId}
            parentObjectName={sourceObjectName}
            recordLinkField={recordLinkField}
            allObjectFields={[]}
          />
        </div>
      </div>
      <div
        className="d-flex justify-content-center py-3 px-2 border-top"
        style={{ background: '#fff' }}
      >
        <Button
          type="button"
          variant="outline-primary"
          className="d-inline-flex align-items-center px-4"
          style={{ borderColor: theme.colors.primary, color: theme.colors.primary }}
          onClick={openShowAllModal}
        >
          <FaListUl className="me-2" aria-hidden />
          {showAllButtonLabel}
        </Button>
      </div>
      <SearchViewShowAllModal
        show={showAllModal}
        onHide={() => setShowAllModal(false)}
        title={fullSearchModalTitle}
        widget={widget}
        recordData={recordData}
        searchViewConfig={searchViewConfig}
        fieldDataTypes={fieldDataTypes}
        fieldMetadata={fieldMetadata}
        searchObjectName={searchObjectName}
        currentRecordId={currentRecordId}
        sourceObjectName={sourceObjectName}
      />
    </div>
  );
};

export default SearchViewRecordsTable;
